import { useCallback, useEffect, useState } from "react";

interface GyroDataItem {
  alpha?: string;
  beta?: string;
  gamma?: string;
  timestamp?: string;
}

export interface IOSDeviceOrientationEvent extends DeviceOrientationEvent {
  requestPermission?: () => Promise<PermissionState>;
}

function useGyroSensor(startGyro: boolean) {
  const [gyroData, setGyroData] = useState<GyroDataItem[]>([]);
  const [permissionGranted, setPermissionGranted] = useState(false);

  const handleOrientation = useCallback((event: DeviceOrientationEvent) => {
    try {
      const { alpha, beta, gamma } = event;
      setGyroData((prev: GyroDataItem[]) => [
        ...prev,
        {
          alpha: alpha?.toString() || undefined,
          beta: beta?.toString() || undefined,
          gamma: gamma?.toString() || undefined,
          timestamp: new Date().toISOString(),
        },
      ]);
    } catch (error) {
      console.log(error);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    const DeviceOrientation =
      DeviceOrientationEvent as unknown as IOSDeviceOrientationEvent;

    if (
      typeof DeviceOrientation !== "undefined" &&
      typeof DeviceOrientation.requestPermission === "function"
    ) {
      try {
        const response = await DeviceOrientation.requestPermission();
        if (response === "granted") {
          setPermissionGranted(true);
        } else {
          console.warn("Device orientation permission denied.");
        }
      } catch (error) {
        console.error("Error requesting device orientation permission:", error);
      }
    } else {
      // Permission not required on non-iOS devices
      setPermissionGranted(true);
    }
  }, []);

  useEffect(() => {
    if (startGyro && permissionGranted) {
      window.addEventListener("deviceorientation", handleOrientation);
    } else {
      setGyroData([]);
    }
    return () => {
      window.removeEventListener("deviceorientation", handleOrientation);
    };
  }, [startGyro, permissionGranted, handleOrientation]);

  useEffect(() => {
    if (startGyro) {
      requestPermission();
    }
  }, [startGyro, requestPermission]);

  return { gyroData };
}

export default useGyroSensor;
